import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { DocumentUploadPanel } from '../components/DocumentUploadPanel';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    Settings, 
    Plus, 
    Eye, 
    CheckCircle, 
    Clock, 
    Filter,
    Ship,
    Truck,
    MessageSquarePlus,
    Calendar,
    FileDown
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const statusLabels = {
    aberto: { label: 'Aberto', class: 'badge-warning' },
    em_andamento: { label: 'Em Andamento', class: 'badge-info' },
    concluido: { label: 'Concluído', class: 'badge-success' }
};

const ManutencaoPage = () => {
    const [pedidos, setPedidos] = useState([]);
    const [empurradores, setEmpurradores] = useState([]);
    const [balsas, setBalsas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [atualizacaoDialogOpen, setAtualizacaoDialogOpen] = useState(false);
    const [selectedPedido, setSelectedPedido] = useState(null);
    const { hasRole, user } = useAuth();

    const [filters, setFilters] = useState({
        status: '',
        embarcacao_tipo: '',
        numero_os: '',
        data_inicio: '',
        data_fim: ''
    });

    const [formData, setFormData] = useState({
        embarcacao_tipo: '',
        embarcacao_id: '',
        descricao: ''
    });

    const [novaAtualizacao, setNovaAtualizacao] = useState('');
    const [gerandoLaudo, setGerandoLaudo] = useState(false);

    const handleGerarLaudo = async (pedido) => {
        if (!pedido?.id) return;
        setGerandoLaudo(true);
        try {
            const r = await axios.get(`${API}/pedidos-reparo/${pedido.id}/laudo-pdf`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `laudo_os_${(pedido.numero_os || '').replace(':', '_')}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Laudo gerado!');
        } catch {
            toast.error('Erro ao gerar laudo');
        } finally {
            setGerandoLaudo(false);
        }
    };

    useEffect(() => {
        fetchBaseData();
    }, []);

    useEffect(() => {
        fetchPedidos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const fetchBaseData = async () => {
        try {
            const [empRes, balsasRes] = await Promise.all([
                axios.get(`${API}/empurradores`),
                axios.get(`${API}/balsas`)
            ]);
            setEmpurradores(empRes.data.filter(e => e.status === 'ativo'));
            setBalsas(balsasRes.data.filter(b => b.status === 'ativa'));
        } catch (error) {
            console.error('Erro ao carregar dados base:', error);
        }
    };

    const fetchPedidos = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.status && filters.status !== 'all') params.status = filters.status;
            if (filters.embarcacao_tipo && filters.embarcacao_tipo !== 'all') params.embarcacao_tipo = filters.embarcacao_tipo;
            if (filters.embarcacao_id && filters.embarcacao_id !== 'all') params.embarcacao_id = filters.embarcacao_id;
            if (filters.numero_os) params.numero_os = filters.numero_os;
            if (filters.data_inicio) params.data_inicio = filters.data_inicio;
            if (filters.data_fim) params.data_fim = filters.data_fim;

            const response = await axios.get(`${API}/pedidos-reparo`, { params });
            setPedidos(response.data);
        } catch (error) {
            toast.error('Erro ao carregar pedidos de reparo');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.embarcacao_tipo || !formData.embarcacao_id) {
            toast.error('Selecione a embarcação solicitante');
            return;
        }

        try {
            await axios.post(`${API}/pedidos-reparo`, formData);
            toast.success('Pedido de Reparo criado!');
            setDialogOpen(false);
            resetForm();
            fetchPedidos();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao criar pedido');
        }
    };

    const handleAddAtualizacao = async () => {
        if (!novaAtualizacao.trim()) {
            toast.error('Digite a atualização');
            return;
        }

        try {
            await axios.post(`${API}/pedidos-reparo/${selectedPedido.id}/atualizacao`, {
                descricao: novaAtualizacao
            });
            toast.success('Atualização adicionada!');
            setNovaAtualizacao('');
            setAtualizacaoDialogOpen(false);
            
            // Recarregar pedido
            const response = await axios.get(`${API}/pedidos-reparo/${selectedPedido.id}`);
            setSelectedPedido(response.data);
            fetchPedidos();
        } catch (error) {
            toast.error('Erro ao adicionar atualização');
        }
    };

    const handleConcluir = async (pedidoId) => {
        if (!window.confirm('Deseja concluir este pedido de reparo?')) return;

        try {
            await axios.put(`${API}/pedidos-reparo/${pedidoId}/concluir`);
            toast.success('Pedido concluído!');
            fetchPedidos();
            if (selectedPedido?.id === pedidoId) {
                const response = await axios.get(`${API}/pedidos-reparo/${pedidoId}`);
                setSelectedPedido(response.data);
            }
        } catch (error) {
            toast.error('Erro ao concluir pedido');
        }
    };

    const handleChangeStatus = async (pedidoId, novoStatus) => {
        try {
            await axios.put(`${API}/pedidos-reparo/${pedidoId}`, { status: novoStatus });
            toast.success('Status atualizado!');
            fetchPedidos();
        } catch (error) {
            toast.error('Erro ao atualizar status');
        }
    };

    const resetForm = () => {
        setFormData({
            embarcacao_tipo: '',
            embarcacao_id: '',
            descricao: ''
        });
    };

    const getEmbarcacaoNome = (tipo, id) => {
        if (tipo === 'empurrador') {
            return empurradores.find(e => e.id === id)?.nome || '-';
        } else {
            return balsas.find(b => b.id === id)?.nome || '-';
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('pt-BR');
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('pt-BR');
    };

    const canEdit = hasRole(['admin', 'gestor', 'operador']);

    if (loading && pedidos.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-cyan-400">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="manutencao-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                        Manutenção - Pedidos de Reparo
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Gerenciamento de ordens de serviço de manutenção
                    </p>
                </div>
                
                {canEdit && (
                    <Dialog open={dialogOpen} onOpenChange={(open) => {
                        setDialogOpen(open);
                        if (!open) resetForm();
                    }}>
                        <DialogTrigger asChild>
                            <Button 
                                data-testid="btn-new-pedido"
                                className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Novo Pedido de Reparo
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-lg">
                            <DialogHeader>
                                <DialogTitle className="font-heading text-xl text-white">
                                    Novo Pedido de Reparo
                                </DialogTitle>
                            </DialogHeader>
                            
                            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                {/* Seção - Bloqueado */}
                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Seção
                                    </Label>
                                    <Input
                                        value="Manutenção"
                                        disabled
                                        className="bg-slate-800 border-slate-700 text-slate-400 rounded-sm cursor-not-allowed"
                                    />
                                </div>

                                {/* Data - Automática */}
                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Data de Abertura
                                    </Label>
                                    <Input
                                        value={new Date().toLocaleDateString('pt-BR')}
                                        disabled
                                        className="bg-slate-800 border-slate-700 text-slate-400 rounded-sm cursor-not-allowed"
                                    />
                                    <p className="text-xs text-slate-500">Preenchida automaticamente</p>
                                </div>

                                {/* Tipo de Embarcação */}
                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Tipo de Embarcação *
                                    </Label>
                                    <Select 
                                        value={formData.embarcacao_tipo || "none"} 
                                        onValueChange={(value) => setFormData({
                                            ...formData, 
                                            embarcacao_tipo: value === "none" ? "" : value,
                                            embarcacao_id: ''
                                        })}
                                    >
                                        <SelectTrigger data-testid="select-embarcacao-tipo" className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                            <SelectValue placeholder="Selecione..." />
                                        </SelectTrigger>
                                        <SelectContent className="bg-slate-900 border-slate-800">
                                            <SelectItem value="none">Selecione o tipo</SelectItem>
                                            <SelectItem value="empurrador">
                                                <div className="flex items-center gap-2">
                                                    <Truck className="w-4 h-4" />
                                                    Empurrador
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="balsa">
                                                <div className="flex items-center gap-2">
                                                    <Ship className="w-4 h-4" />
                                                    Balsa
                                                </div>
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Embarcação */}
                                {formData.embarcacao_tipo && (
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            {formData.embarcacao_tipo === 'empurrador' ? 'Empurrador' : 'Balsa'} Solicitante *
                                        </Label>
                                        <Select 
                                            value={formData.embarcacao_id || "none"} 
                                            onValueChange={(value) => setFormData({...formData, embarcacao_id: value === "none" ? "" : value})}
                                        >
                                            <SelectTrigger data-testid="select-embarcacao" className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                <SelectValue placeholder="Selecione..." />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                <SelectItem value="none">Selecione a embarcação</SelectItem>
                                                {formData.embarcacao_tipo === 'empurrador' ? (
                                                    empurradores.map((emp) => (
                                                        <SelectItem key={emp.id} value={emp.id}>{emp.nome}</SelectItem>
                                                    ))
                                                ) : (
                                                    balsas.map((balsa) => (
                                                        <SelectItem key={balsa.id} value={balsa.id}>{balsa.nome}</SelectItem>
                                                    ))
                                                )}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                {/* Descrição */}
                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Descrição do Problema *
                                    </Label>
                                    <Textarea
                                        data-testid="input-descricao"
                                        value={formData.descricao}
                                        onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                                        required
                                        placeholder="Descreva detalhadamente o problema ou serviço necessário..."
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[100px]"
                                    />
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            setDialogOpen(false);
                                            resetForm();
                                        }}
                                        className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        data-testid="btn-save-pedido"
                                        className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                    >
                                        Abrir Pedido
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* Filtros */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-4">
                        <Filter className="w-4 h-4 text-cyan-400" />
                        <span className="text-sm font-mono uppercase tracking-wider text-cyan-400">Filtros</span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Período - Início</Label>
                            <Input
                                type="date"
                                value={filters.data_inicio}
                                onChange={(e) => setFilters({...filters, data_inicio: e.target.value})}
                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Período - Fim</Label>
                            <Input
                                type="date"
                                value={filters.data_fim}
                                onChange={(e) => setFilters({...filters, data_fim: e.target.value})}
                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Tipo</Label>
                            <Select 
                                value={filters.embarcacao_tipo || "all"} 
                                onValueChange={(value) => setFilters({...filters, embarcacao_tipo: value === "all" ? "" : value, embarcacao_id: ''})}
                            >
                                <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9">
                                    <SelectValue placeholder="Todos" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-800">
                                    <SelectItem value="all">Todos</SelectItem>
                                    <SelectItem value="empurrador">Empurrador</SelectItem>
                                    <SelectItem value="balsa">Balsa</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Embarcação</Label>
                            <Select 
                                value={filters.embarcacao_id || "all"} 
                                onValueChange={(value) => setFilters({...filters, embarcacao_id: value === "all" ? "" : value})}
                            >
                                <SelectTrigger data-testid="filter-embarcacao" className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9">
                                    <SelectValue placeholder="Todas" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-800">
                                    <SelectItem value="all">Todas</SelectItem>
                                    {(filters.embarcacao_tipo === '' || filters.embarcacao_tipo === 'empurrador') && empurradores.map(emp => (
                                        <SelectItem key={emp.id} value={emp.id}>{emp.nome}</SelectItem>
                                    ))}
                                    {(filters.embarcacao_tipo === '' || filters.embarcacao_tipo === 'balsa') && balsas.map(balsa => (
                                        <SelectItem key={balsa.id} value={balsa.id}>{balsa.nome}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Status</Label>
                            <Select 
                                value={filters.status || "all"} 
                                onValueChange={(value) => setFilters({...filters, status: value === "all" ? "" : value})}
                            >
                                <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9">
                                    <SelectValue placeholder="Todos" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-800">
                                    <SelectItem value="all">Todos</SelectItem>
                                    <SelectItem value="aberto">Aberto</SelectItem>
                                    <SelectItem value="em_andamento">Em Andamento</SelectItem>
                                    <SelectItem value="concluido">Concluído</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Nº da OS</Label>
                            <Input
                                placeholder="Ex: 001:2026"
                                value={filters.numero_os}
                                onChange={(e) => setFilters({...filters, numero_os: e.target.value})}
                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9"
                            />
                        </div>
                        <div className="flex items-end">
                            <Button 
                                onClick={() => setFilters({ status: '', embarcacao_tipo: '', numero_os: '', data_inicio: '', data_fim: '' })}
                                variant="outline"
                                className="w-full border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm h-9"
                            >
                                Limpar
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="flex items-center gap-4 p-4">
                        <div className="p-2 bg-blue-500/20 rounded-sm">
                            <Settings className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">{pedidos.length}</p>
                            <p className="text-xs text-slate-400">Total de Pedidos</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="flex items-center gap-4 p-4">
                        <div className="p-2 bg-yellow-500/20 rounded-sm">
                            <Clock className="w-5 h-5 text-yellow-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">
                                {pedidos.filter(p => p.status === 'aberto').length}
                            </p>
                            <p className="text-xs text-slate-400">Abertos</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="flex items-center gap-4 p-4">
                        <div className="p-2 bg-cyan-500/20 rounded-sm">
                            <Settings className="w-5 h-5 text-cyan-400 animate-spin-slow" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">
                                {pedidos.filter(p => p.status === 'em_andamento').length}
                            </p>
                            <p className="text-xs text-slate-400">Em Andamento</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="flex items-center gap-4 p-4">
                        <div className="p-2 bg-green-500/20 rounded-sm">
                            <CheckCircle className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">
                                {pedidos.filter(p => p.status === 'concluido').length}
                            </p>
                            <p className="text-xs text-slate-400">Concluídos</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Table */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardContent className="p-0">
                    {pedidos.length === 0 ? (
                        <div className="p-12 text-center">
                            <Settings className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                            <p className="text-slate-400">Nenhum pedido de reparo encontrado</p>
                        </div>
                    ) : (
                        <Table className="table-naval">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Nº OS</TableHead>
                                    <TableHead>Data</TableHead>
                                    <TableHead>Embarcação</TableHead>
                                    <TableHead>Descrição</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Conclusão</TableHead>
                                    <TableHead>Ações</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {pedidos.map((pedido) => (
                                    <TableRow key={pedido.id} data-testid={`pedido-row-${pedido.id}`}>
                                        <TableCell className="font-mono text-cyan-400 font-bold">
                                            {pedido.numero_os}
                                        </TableCell>
                                        <TableCell className="text-slate-400 text-sm">
                                            {formatDate(pedido.data_abertura)}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                {pedido.embarcacao_tipo === 'empurrador' ? (
                                                    <Truck className="w-4 h-4 text-slate-500" />
                                                ) : (
                                                    <Ship className="w-4 h-4 text-slate-500" />
                                                )}
                                                <span className="text-white">
                                                    {getEmbarcacaoNome(pedido.embarcacao_tipo, pedido.embarcacao_id)}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-slate-300 max-w-[200px] truncate">
                                            {pedido.descricao}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`
                                                px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                                ${statusLabels[pedido.status]?.class || 'badge-info'}
                                            `}>
                                                {statusLabels[pedido.status]?.label || pedido.status}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-slate-400 text-sm">
                                            {pedido.data_conclusao ? formatDate(pedido.data_conclusao) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedPedido(pedido);
                                                        setDetailDialogOpen(true);
                                                    }}
                                                    className="text-slate-400 hover:text-white hover:bg-slate-800"
                                                    title="Ver detalhes"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                                {pedido.status !== 'concluido' && canEdit && (
                                                    <>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => {
                                                                setSelectedPedido(pedido);
                                                                setAtualizacaoDialogOpen(true);
                                                            }}
                                                            className="text-cyan-400 hover:text-cyan-300 hover:bg-slate-800"
                                                            title="Adicionar atualização"
                                                        >
                                                            <MessageSquarePlus className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleConcluir(pedido.id)}
                                                            className="text-green-400 hover:text-green-300 hover:bg-slate-800"
                                                            title="Concluir pedido"
                                                        >
                                                            <CheckCircle className="w-4 h-4" />
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Detail Dialog */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                            <Settings className="w-5 h-5 text-cyan-400" />
                            Pedido de Reparo - {selectedPedido?.numero_os}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedPedido && (
                        <div className="space-y-6 mt-4">
                            {/* Botão Gerar Laudo PDF */}
                            <div className="flex justify-end">
                                <Button
                                    onClick={() => handleGerarLaudo(selectedPedido)}
                                    disabled={gerandoLaudo}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-8 text-xs uppercase tracking-wider font-mono"
                                    data-testid="btn-laudo-pdf"
                                >
                                    <FileDown className="w-4 h-4 mr-2" />
                                    {gerandoLaudo ? 'Gerando...' : 'Gerar Laudo PDF'}
                                </Button>
                            </div>

                            {/* Documentos / Anexos */}
                            <DocumentUploadPanel
                                modulo="manutencao"
                                entidadeId={selectedPedido.id}
                                entidadeNome={selectedPedido.numero_os}
                            />

                            {/* Info Geral */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Nº da OS</p>
                                    <p className="text-cyan-400 font-mono font-bold mt-1">{selectedPedido.numero_os}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Data de Abertura</p>
                                    <p className="text-white mt-1">{formatDate(selectedPedido.data_abertura)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Seção</p>
                                    <p className="text-white mt-1">{selectedPedido.secao}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Status</p>
                                    <div className="mt-1">
                                        {selectedPedido.status !== 'concluido' && canEdit ? (
                                            <Select 
                                                value={selectedPedido.status} 
                                                onValueChange={(value) => handleChangeStatus(selectedPedido.id, value)}
                                            >
                                                <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200 rounded-sm h-8 w-40">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-slate-800">
                                                    <SelectItem value="aberto">Aberto</SelectItem>
                                                    <SelectItem value="em_andamento">Em Andamento</SelectItem>
                                                    <SelectItem value="concluido">Concluído</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <span className={`
                                                px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                                ${statusLabels[selectedPedido.status]?.class}
                                            `}>
                                                {statusLabels[selectedPedido.status]?.label}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Embarcação */}
                            <div className="p-4 bg-slate-800/50 rounded-sm">
                                <p className="text-xs text-slate-500 font-mono uppercase mb-2">Embarcação Solicitante</p>
                                <div className="flex items-center gap-3">
                                    {selectedPedido.embarcacao_tipo === 'empurrador' ? (
                                        <Truck className="w-6 h-6 text-cyan-400" />
                                    ) : (
                                        <Ship className="w-6 h-6 text-cyan-400" />
                                    )}
                                    <div>
                                        <p className="text-white font-medium">
                                            {getEmbarcacaoNome(selectedPedido.embarcacao_tipo, selectedPedido.embarcacao_id)}
                                        </p>
                                        <p className="text-xs text-slate-500 capitalize">{selectedPedido.embarcacao_tipo}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Descrição */}
                            <div>
                                <p className="text-xs text-slate-500 font-mono uppercase mb-2">Descrição do Problema</p>
                                <p className="text-slate-300 bg-slate-800/30 p-3 rounded-sm">{selectedPedido.descricao}</p>
                            </div>

                            {/* Data de Conclusão */}
                            {selectedPedido.data_conclusao && (
                                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-sm">
                                    <div className="flex items-center gap-2">
                                        <CheckCircle className="w-5 h-5 text-green-400" />
                                        <div>
                                            <p className="text-green-400 font-medium">Concluído</p>
                                            <p className="text-sm text-slate-400">Data: {formatDate(selectedPedido.data_conclusao)}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Atualizações */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-xs text-slate-500 font-mono uppercase flex items-center gap-2">
                                        <MessageSquarePlus className="w-4 h-4" />
                                        Atualizações do Pedido ({selectedPedido.atualizacoes?.length || 0})
                                    </p>
                                    {selectedPedido.status !== 'concluido' && canEdit && (
                                        <Button
                                            size="sm"
                                            onClick={() => setAtualizacaoDialogOpen(true)}
                                            className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm h-7 text-xs"
                                        >
                                            <Plus className="w-3 h-3 mr-1" />
                                            Nova Atualização
                                        </Button>
                                    )}
                                </div>
                                
                                {selectedPedido.atualizacoes && selectedPedido.atualizacoes.length > 0 ? (
                                    <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                                        {selectedPedido.atualizacoes.map((atualizacao, idx) => (
                                            <div key={atualizacao.atualizacao_id || idx} className="p-3 bg-slate-800/50 rounded-sm border-l-2 border-cyan-500">
                                                <div className="flex items-start justify-between mb-1">
                                                    <span className="text-xs text-slate-500">
                                                        {formatDateTime(atualizacao.data)}
                                                    </span>
                                                    <span className="text-xs text-cyan-400 font-mono">
                                                        {atualizacao.usuario}
                                                    </span>
                                                </div>
                                                <p className="text-slate-300 text-sm">{atualizacao.descricao}</p>
                                                {atualizacao.atualizacao_id && (
                                                    <div className="mt-2">
                                                        <DocumentUploadPanel
                                                            modulo="manutencao_atualizacao"
                                                            entidadeId={atualizacao.atualizacao_id}
                                                            entidadeNome={`Atualização ${idx + 1}`}
                                                            compact={true}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-slate-500 text-sm italic">Nenhuma atualização registrada</p>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Atualização Dialog */}
            <Dialog open={atualizacaoDialogOpen} onOpenChange={(open) => {
                setAtualizacaoDialogOpen(open);
                if (!open) setNovaAtualizacao('');
            }}>
                <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                            <MessageSquarePlus className="w-5 h-5 text-cyan-400" />
                            Adicionar Atualização
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        <p className="text-sm text-slate-400">
                            OS: <span className="text-cyan-400 font-mono">{selectedPedido?.numero_os}</span>
                        </p>
                        
                        <div className="space-y-2">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                Descrição da Atualização *
                            </Label>
                            <Textarea
                                value={novaAtualizacao}
                                onChange={(e) => setNovaAtualizacao(e.target.value)}
                                placeholder="Descreva o andamento, intervenção ou observação técnica..."
                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[100px]"
                            />
                        </div>

                        <div className="flex gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setAtualizacaoDialogOpen(false);
                                    setNovaAtualizacao('');
                                }}
                                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleAddAtualizacao}
                                className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                            >
                                Adicionar
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ManutencaoPage;
